import { isEmpty, isUndefined } from "lodash";
import cleanComponents from "utils/cleanComponents";
import { getObjValAtPath } from "utils/fields";
import { calcBuildingSurfaceArea } from "utils/enclosure/geometry";
import { DIRECTIONAL_COMPONENT_TYPES } from "utils/enclosure/filters";

const codeRefMap = {
    Wall: {
        path: ["wallInsType.codeRef"],
        componentType: ["wall"],
    },
    BasementWall: {
        path: ["wall.intAddedInsType.codeRef"],
        componentType: ["basement"],
    },
    Ceiling: {
        path: ["ceilingInsType.codeRef"],
        componentType: ["ceiling"],
    },
    CeilingFlat: {
        path: ["ceilingInsType.codeRef"],
        componentType: ["ceiling"],
    },
    CrawlspaceWall: {
        path: ["wall.crawlspaceWallInsType.codeRef", "wall.ponyWall.ponyWallInsType.codeRef"],
        componentType: ["crawlspace", "basement"],
    },
    Floor: {
        path: ["expFloorInsType.codeRef"],
        componentType: ["expFloor"],
    },
    FloorsAbove: {
        path: ["floor.floorsAboveInsType.codeRef", "floor.floorsAboveInsType.codeRef"],
        componentType: ["basement", "crawlspace"],
    },
    FloorsAdded: {
        path: ["floor.slabInsType.codeRef", "floor.slabInsType.codeRef", "floor.slabInsType.codeRef"],
        componentType: ["basement", "crawlspace", "slab"],
    },
    Lintel: {
        path: ["lintelInsType.codeRef", "wall.lintelInsType.codeRef", "wall.lintelInsType.codeRef"],
        componentType: ["wall", "basement", "crawlspace"],
    },
};

// export const getMatchCodeRefs = (components, newCodeRef, editingCodeRef) => {
//     const { path = '', componentType = '' } = codeRefMap[newCodeRef.split('-')[0]];

//     if (isEmpty(components[componentType]) || isEmpty(editingCodeRef)) {
//         return [];
//     }

//     return Object.keys(components[componentType]).reduce((cache, compKey) => {
//         const foundCodeRef = path.split('.').reduce((acc, curr) => acc[curr], components[componentType][compKey]);
//         if (foundCodeRef === editingCodeRef) {
//             return [...cache, `model.modelData.components.${componentType}.${compKey}.${path.split('.codeRef')[0]}`];
//         } else {
//             return cache;
//         }
//     }, []);
// };

export const getMatchCodeRefs = (components, newCodeRef, editingCodeRef) => {
    if (newCodeRef.includes("FloorHeader") || editingCodeRef.includes("FloorHeader")) {
        return getMatchFloorHeaderCodeRefs(components, editingCodeRef);
    }

    const mapEntry = codeRefMap[newCodeRef.split("-")[0]];
    if (!mapEntry) {
        return [];
    }

    const { path: pathArray = [], componentType = [] } = mapEntry;

    const accessorList = pathArray.reduce((acc, path, ind) => {
        if (isEmpty(components[componentType[ind]]) || isEmpty(editingCodeRef)) {
            return acc;
        }

        return [
            ...acc,
            ...Object.keys(components[componentType[ind]]).reduce((cache, compKey) => {
                const root = components[componentType[ind]][compKey];
                const foundCodeRef = path
                    .split(".")
                    .reduce((walkAcc, curr) => walkAcc?.[curr], root);
                if (foundCodeRef === editingCodeRef) {
                    return [
                        ...cache,
                        `model.modelData.components.${componentType[ind]}.${compKey}.${path.split(".codeRef")[0]}`,
                    ];
                } else {
                    return cache;
                }
            }, []),
        ];
    }, []);

    return accessorList;
};

export const getMatchFloorHeaderCodeRefs = (components, editingCodeRef) => {
    const { wall = {}, basement = {}, crawlspace = {} } = components;

    let accessorsToUpdate = [];
    if (!isEmpty(wall)) {
        Object.entries(wall).forEach(([wallKey, wallObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = wallObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.wall.${wallKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(basement)) {
        Object.entries(basement).forEach(([basementKey, basementObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = basementObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.basement.${basementKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(crawlspace)) {
        Object.entries(crawlspace).forEach(([crawlspaceKey, crawlspaceObj]) => {
            const { subcomponents: { floorHeader = {} } = {} } = crawlspaceObj;

            if (!isEmpty(floorHeader)) {
                Object.keys(floorHeader).forEach((floorHeaderKey) => {
                    const { floorHeaderInsType: { codeRef: floorHeaderCodeRef = "" } = {} } =
                        floorHeader[floorHeaderKey] || {};

                    if (floorHeaderCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.floorHeader.${floorHeaderKey}.floorHeaderInsType`,
                        ];
                    }
                });
            }
        });
    }

    return accessorsToUpdate;
};

export const getAllComponents = (componentType, components, currentComponent = "") =>
    Object.keys(components)
        .reduce((cache, key) => {
            // get id and location
            const cleanedComps = cleanComponents(components);
            const parentComp = cleanedComps[key] || {};

            let componentArray = Object.keys(parentComp).map((componentId) => {
                const { label = "", constructionType: { id: constructionTypeId } = {} } = parentComp[componentId] || {};
                const value = `${key}.${componentId}`;

                const isCurrentComponent = currentComponent !== "" && currentComponent.includes(value);

                return {
                    label,
                    value,
                    isFlat: [2, 3].includes(constructionTypeId),
                    disabled: isCurrentComponent,
                };
            });

            if (componentType === "ceiling-flat") {
                componentArray = componentArray.filter(({ isFlat }) => isFlat);
            } else if (componentType === "ceiling") {
                componentArray = componentArray.filter(({ isFlat }) => !isFlat);
            }

            return [...cache, ...componentArray];
        }, [])
        .filter(({ value = "" }) => value.includes(componentType.split("-")[0])); //splitting handles "ceiling-flat"

//Only call this with 'component' === 'window' 'door' or 'floorHeader'
export const getAllSubcomponents = (componentType, components, currentComponent = "") =>
    Object.keys(components)
        .reduce((cache, key) => {
            const cleanedComps = cleanComponents(components);
            //this returns a flat array with value (accessor) and label of subcomponents

            const parentComp = cleanedComps[key] || {};
            const subcomponents = Object.keys(parentComp).reduce((compCache, parentKey) => {
                const { subcomponents: { [componentType]: subComps = {}, door: doorObj = {} } = {} } =
                    parentComp[parentKey] || {};
                return [
                    ...compCache,
                    ...Object.keys(subComps).reduce((subCache, subCompKey) => {
                        return [
                            ...subCache,
                            {
                                value: `${key}.${parentKey}.subcomponents.${componentType}.${subCompKey}`,
                                label: subComps[subCompKey].label,
                            },
                        ];
                    }, []),
                    ...(componentType === "window"
                        ? getWindowsOnDoors(doorObj, `${key}.${parentKey}.subcomponents`)
                        : []),
                ];
            }, []);

            const subComponentArray = subcomponents.map((subcomp) => {
                const { label = "", value = "" } = subcomp;

                const isCurrentComponent = currentComponent !== "" && currentComponent.includes(value);

                return {
                    label,
                    value,
                    disabled: isCurrentComponent,
                };
            });

            return [...cache, ...subComponentArray];
        }, [])
        .filter(({ value = "" }) => value.includes(componentType));

const getWindowsOnDoors = (doorsObj, currentPath) => {
    return Object.keys(doorsObj).reduce((cache, doorKey) => {
        const { subcomponents: { window = {} } = {} } = doorsObj[doorKey] || {};
        return [
            ...cache,
            ...Object.keys(window).reduce((subCache, subCompKey) => {
                return [
                    ...subCache,
                    {
                        value: `${currentPath}.door.${doorKey}.subcomponents.window.${subCompKey}`,
                        label: window[subCompKey].label,
                    },
                ];
            }, []),
        ];
    }, []);
};

export const getSubComponentRenderProps = ({
    componentType = "",
    parentAccessor = "",
    parentPath = "",
    isInterior = false,
} = {}) => {
    if (!["door", "floorHeader", "window"].includes(componentType)) {
        return {};
    }

    const isSubSubComponent = componentType === "window" && parentPath.includes(".door.");

    return {
        parentAccessor,
        parentPath,
        isSubComponent: !isSubSubComponent,
        isInteriorChild: isInterior === true,
        ...(isSubSubComponent ? { isSubSubComponent: true } : {}),
    };
};

export const getCompAreaForCostCalc = (selectedComponents = [], components = {}, compType = "", calcType = "") => {
    //Where selectedComponents is an array of accessors like ["ceiling.ceiling001", "ceiling.ceiling002"]
    //components is the object at modelData.components
    //compType aligns with "upgradeType"

    if (compType === "airTightness") {
        return calcBuildingSurfaceArea(components, true);
    }

    if (isEmpty(selectedComponents) || isEmpty(components) || compType === "") return 0;

    return selectedComponents
        .map((compAccessor) => {
            if (["basementSlab", "slab"].includes(compType)) {
                const { measurements: { isRectangular, length = 0, width = 0, area = 0 } = {} } =
                    getObjValAtPath(components, `${compAccessor}.floor`) || {};

                return isRectangular ? length * width : area;
            } else if (["basementWall", "crawlspace"].includes(compType)) {
                const { measurements: { isRectangular, length = 0, width = 0, perimeter = 0 } = {} } =
                    getObjValAtPath(components, `${compAccessor}.floor`) || {};
                const { measurements: { height = 0 } = {} } = getObjValAtPath(components, `${compAccessor}.wall`) || {};

                return isRectangular ? height * 2 * (length + width) : height * perimeter;
            } else if (["window", "skylights"].includes(compType)) {
                const { numIdentical = 1, measurements: { width = 0, height = 0 } = {} } =
                    getObjValAtPath(components, compAccessor) || {};
                if (calcType === "matCostArea") {
                    return (numIdentical * (width * height)) / 1000000; //mm2 to m2
                } else {
                    //By default, window returns count
                    return 1 * numIdentical;
                }
            } else if (compType === "door") {
                const { measurements: { width = 0, height = 0 } = {} } =
                    getObjValAtPath(components, compAccessor) || {};
                if (calcType === "matCostArea") {
                    return width * height; //area
                } else {
                    return 1; //count
                }
            }

            const { measurements: { area } = {} } = getObjValAtPath(components, compAccessor) || {};

            return area;
        })
        .reduce((sum, curr) => sum + curr, 0);
};

export const calcWindowToWallRatio = (components = {}) => {
    const { wall = {}, basement = {}, crawlspace = {} } = components;

    let totalAgWallArea = 0;
    let totalBsmtCrwlAgWallArea = 0;
    let totalFloorHeaderArea = 0;
    let totalWindowArea = 0;
    let totalDoorArea = 0;
    Object.keys(basement).forEach((bsmtKey) => {
        const {
            exposedSurfacePerimeter = 0,
            // floor: { isRectangular = true, area = 0, width = 0, perimeter = 0, length = 0 } = {},
            wall: { measurements: { height = 0, depth = 0 } = {} } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = basement[bsmtKey] || {};

        const totalExposedPerimeter = exposedSurfacePerimeter;
        const currentAgArea = totalExposedPerimeter * (height - depth);
        totalBsmtCrwlAgWallArea = totalBsmtCrwlAgWallArea + currentAgArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    Object.keys(crawlspace).forEach((crwlKey) => {
        const {
            exposedSurfacePerimeter = 0,
            // floor: { isRectangular = true, area = 0, width = 0, perimeter = 0, length = 0 } = {},
            wall: { measurements: { height = 0, depth = 0 } = {} } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = crawlspace[crwlKey] || {};

        const totalExposedPerimeter = exposedSurfacePerimeter;
        const currentAgArea = totalExposedPerimeter * (height - depth);
        totalBsmtCrwlAgWallArea = totalBsmtCrwlAgWallArea + currentAgArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    Object.keys(wall).forEach((wallKey) => {
        const {
            measurements: { height = 0, perimeter = 0 } = {},
            subcomponents: {
                window: windowsOnWall = {},
                door: doorsOnWall = {},
                floorHeader: floorHeaderOnWall = {},
            } = {},
        } = wall[wallKey] || {};

        const currWallArea = height * perimeter;
        totalAgWallArea = totalAgWallArea + currWallArea;

        Object.keys(windowsOnWall).forEach((windowKey) => {
            const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                windowsOnWall[windowKey] || {};
            const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

            totalWindowArea = totalWindowArea + currentWinArea;
        });

        Object.keys(doorsOnWall).forEach((doorKey) => {
            const {
                measurements: { height: doorHeight = 0, width: doorWidth = 0 } = {},
                subcomponents: { window: windowsOnDoor = {} } = {},
            } = doorsOnWall[doorKey] || {};
            totalDoorArea = totalDoorArea + doorHeight * doorWidth; // [area in m2]

            Object.keys(windowsOnDoor).forEach((windowKey) => {
                const { numIdentical = 1, measurements: { height: winHeight = 0, width: winWidth = 0 } = {} } =
                    windowsOnDoor[windowKey] || {};
                const currentWinArea = (numIdentical * (winHeight * winWidth)) / 1000000; // [area in m2]

                totalWindowArea = totalWindowArea + currentWinArea;
            });
        });

        Object.keys(floorHeaderOnWall).forEach((fhKey) => {
            const { measurements: { height: fhHeight = 0, perimeter: fhPerimeter = 0 } = {} } =
                floorHeaderOnWall[fhKey] || {};
            totalFloorHeaderArea = totalFloorHeaderArea + fhHeight * fhPerimeter; // [area in m2]
        });
    });

    let wwr = totalWindowArea / (totalAgWallArea + totalBsmtCrwlAgWallArea + totalFloorHeaderArea);

    return wwr;
};

const FACING_DIRECTION_ROOT_KEYS = ["components", "intComponents"];

/**
 * Split a model.modelData.(components|intComponents).*.facingDirection path into
 * its root key and the component path relative to that root.
 */
const splitFacingDirectionPath = (path = "") => {
    if (!path.endsWith(".facingDirection")) {
        return null;
    }

    const rootKey = FACING_DIRECTION_ROOT_KEYS.find((key) => path.startsWith(`model.modelData.${key}.`));
    if (!rootKey) {
        return null;
    }

    const relativePath = path
        .slice(`model.modelData.${rootKey}.`.length)
        .replace(/\.facingDirection$/, "");

    return { rootKey, relativePath };
};

/** Parse component type from a model.(components|intComponents).*.facingDirection change path. */
export const parseComponentFacingDirectionPath = (path = "") => {
    const { relativePath } = splitFacingDirectionPath(path) || {};
    if (!relativePath) {
        return null;
    }

    const parts = relativePath.split(".");

    if (parts.length < 2) {
        return null;
    }

    if (parts.includes("subcomponents")) {
        const subIdx = parts.lastIndexOf("subcomponents");
        const componentType = parts[subIdx + 1];
        return DIRECTIONAL_COMPONENT_TYPES.includes(componentType) ? componentType : null;
    }

    const componentType = parts[0];
    return DIRECTIONAL_COMPONENT_TYPES.includes(componentType) ? componentType : null;
};

export const WALL_COMPASS_FACING_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
export const DEFAULT_HOUSE_FACING_ID = 4;

/** True when a wall has a compass direction assigned (not N/A). */
export const wallHasAssignedDirection = (facingId) => WALL_COMPASS_FACING_IDS.includes(facingId);

/** True when a door has a compass direction assigned (not undefined). */
export const doorHasAssignedDirection = (facingId) => !isUndefined(facingId);

/** House facingDirection.id (0–7), falling back to North when missing or invalid. */
export const resolveHouseFacingId = (houseFacingId) => {
    if (houseFacingId == null || Number.isNaN(houseFacingId) || houseFacingId < 0 || houseFacingId > 7) {
        return DEFAULT_HOUSE_FACING_ID;
    }

    return houseFacingId;
};

/** Map house facingDirection.id (0–7) to a subcomponent facingDirection.id. */
export const convertHouseFacingIdToSubcomponent = (componentType, houseFacingId) => {
    const id = resolveHouseFacingId(houseFacingId);

    if (componentType === "window" || componentType === "door") {
        return id;
    }

    if (componentType === "floorHeader") {
        return id + 1;
    }

    return null;
};

/** Apply house direction to newly created subcomponent data (wall is N/A). */
export const applyHouseDirectionToNewSubcomponent = (componentData, componentType, houseFacingId) => {
    const converted = convertHouseFacingIdToSubcomponent(componentType, houseFacingId);
    if (converted == null) {
        return componentData;
    }

    return {
        ...componentData,
        facingDirection: { id: converted },
    };
};

/** Apply a wall's compass direction to newly created subcomponent data. */
export const applyWallDirectionToNewSubcomponent = (componentData, componentType, wallFacingId) => {
    const inheritedId = convertWallFacingIdToSubcomponent(componentType, wallFacingId);
    if (inheritedId == null) {
        return componentData;
    }

    return {
        ...componentData,
        facingDirection: { id: inheritedId },
    };
};

/** Map a wall facingDirection.id to the equivalent subcomponent id. */
export const convertWallFacingIdToSubcomponent = (componentType, wallFacingId) => {
    if (!wallHasAssignedDirection(wallFacingId)) {
        return null;
    }

    if (componentType === "window" || componentType === "door") {
        return wallFacingId - 1;
    }

    if (componentType === "floorHeader") {
        return wallFacingId;
    }

    return null;
};

const applyFacingDirectionToSubcomponents = (subcomponents = {}, convertFn) => {
    const { window = {}, door = {}, floorHeader = {} } = subcomponents;

    Object.values(window).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("window");
        if (converted != null) {
            component.facingDirection.id = converted;
        }
    });

    Object.values(floorHeader).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("floorHeader");
        if (converted != null) {
            component.facingDirection.id = converted;
        }
    });

    Object.values(door).forEach((component) => {
        if (!component?.facingDirection) return;

        const converted = convertFn("door");
        if (converted != null) {
            component.facingDirection.id = converted;
        }

        Object.values(component.subcomponents?.window || {}).forEach((win) => {
            if (!win?.facingDirection) return;

            const winConverted = convertFn("window");
            if (winConverted != null) {
                win.facingDirection.id = winConverted;
            }
        });
    });
};

const applyWallDirectionToSubcomponents = (subcomponents = {}, wallFacingId) =>
    applyFacingDirectionToSubcomponents(subcomponents, (componentType) =>
        convertWallFacingIdToSubcomponent(componentType, wallFacingId)
    );

/**
 * When a wall gets a compass direction, propagate it to all subcomponents.
 * When the wall is set to N/A, seed subcomponents from the wall's previous compass direction.
 */
export const syncWallSubcomponentDirections = (
    draft,
    path,
    facingDirectionValue,
    previousFacingDirectionValue
) => {
    if (parseComponentFacingDirectionPath(path) !== "wall") {
        return;
    }

    const wallFacingId = facingDirectionValue?.id;

    const { rootKey, relativePath } = splitFacingDirectionPath(path) || {};
    const [, wallKey, ...rest] = relativePath?.split(".") || [];
    if (!wallKey || rest.length) {
        return;
    }

    const wall = draft?.model?.modelData?.[rootKey]?.wall?.[wallKey];
    if (!wall?.subcomponents) {
        return;
    }

    if (wallHasAssignedDirection(wallFacingId)) {
        applyWallDirectionToSubcomponents(wall.subcomponents, wallFacingId);
        return;
    }

    if (wallFacingId === 0) {
        const previousWallFacingId = previousFacingDirectionValue?.id;

        if (wallHasAssignedDirection(previousWallFacingId)) {
            applyWallDirectionToSubcomponents(wall.subcomponents, previousWallFacingId);
        }
    }
};

/**
 * When a door direction changes, propagate it to windows on that door.
 * Skipped when the parent wall has an assigned direction (wall sync is the source of truth).
 */
export const syncDoorSubcomponentDirections = (draft, path, facingDirectionValue) => {
    if (parseComponentFacingDirectionPath(path) !== "door") {
        return;
    }

    const doorFacingId = facingDirectionValue?.id;
    if (doorFacingId == null || Number.isNaN(doorFacingId)) {
        return;
    }

    const { rootKey, relativePath } = splitFacingDirectionPath(path) || {};
    if (!relativePath) {
        return;
    }

    const rootComponents = draft?.model?.modelData?.[rootKey];
    const parts = relativePath.split(".");

    if (parts[0] === "wall" && parts.length >= 4) {
        const wallKey = parts[1];
        const wall = rootComponents?.wall?.[wallKey];
        if (wallHasAssignedDirection(wall?.facingDirection?.id)) {
            return;
        }
    }

    const door = getObjValAtPath(rootComponents, relativePath);
    const windows = door?.subcomponents?.window || {};

    Object.values(windows).forEach((win) => {
        if (win?.facingDirection) {
            win.facingDirection.id = doorFacingId;
        }
    });
};

/** True when a subcomponent's direction is inherited from its parent wall/door and should not be edited. */
export const isSubcomponentDirectionLocked = (
    componentType,
    { mainCompFacingId, parentFacingId, isDoorWindow } = {}
) => {
    if (isDoorWindow) {
        return doorHasAssignedDirection(parentFacingId) || wallHasAssignedDirection(mainCompFacingId);
    }
    return wallHasAssignedDirection(mainCompFacingId);
};

export const mergeExtIntComponents = (componentsObj = {}, intComponentsObj = {}) => {
    const merged = { ...componentsObj };
    // Merge interior component types
    ["wall", "ceiling", "expFloor"].forEach(type => {
        if (intComponentsObj[type]) {
            merged[type] = { ...(merged[type] || {}), ...intComponentsObj[type] };
        }
    });
    return merged;
};

export const flattenComponents = (components = {}) => {
    return Object.entries(components || {}).reduce((acc, [componentType, componentList]) => {
        var allSubComponents = {};
        const mainComponents = Object.fromEntries(
            Object.entries(componentList || {}).map(([componentId, componentData]) => {
                const subComponents = flattenComponents(componentData?.subcomponents || {});
                allSubComponents = { ...allSubComponents, ...subComponents };
                return [componentId, { ...componentData, componentType, subComponents }];
            })
        );

        return {
            ...acc,
            ...mainComponents,
            ...allSubComponents,
        };
    }, {});
};